import { listOrgEnrollments } from "./enrollmentHelpers"
import { listOrgCertificates } from "./certHelpers"
import { listOrgOrders } from "./orderHelpers"

// All program-detail aggregations scope down from the org-wide collections
// by filtering on the program's bundled courseIds — same "fetch everything,
// filter in memory" tradeoff already made throughout dashboardAnalyticsHelpers.js.

export const getProgramLearners = async (orgId, courseIds) => {
  const { enrollments, error } = await listOrgEnrollments(orgId)
  if (error) return { enrollments: [], error }
  return { enrollments: enrollments?.filter((e) => courseIds?.includes(e?.courseId)) || [], error: null }
}

export const getProgramCertificates = async (orgId, courseIds) => {
  const { certificates, error } = await listOrgCertificates(orgId)
  if (error) return { certificates: [], error }
  return { certificates: certificates?.filter((c) => courseIds?.includes(c?.courseId)) || [], error: null }
}

// Cohorts only exist at the course level (see enrollmentHelpers.assignCohort) —
// this aggregates cohort names across the program's bundled courses rather
// than introducing a separate program-level cohort concept.
export const getProgramCohorts = async (orgId, courseIds) => {
  const { enrollments } = await getProgramLearners(orgId, courseIds)
  const byCohort = {}
  enrollments?.forEach((e) => {
    const name = e?.cohortName?.trim()
    if (!name) return
    if (!byCohort[name]) byCohort[name] = { name, learnerIds: new Set(), progressSum: 0, count: 0 }
    byCohort[name].learnerIds.add(e?.userId)
    byCohort[name].progressSum += e?.progressPercent || 0
    byCohort[name].count += 1
  })
  return Object.values(byCohort)
    ?.map((c) => ({ name: c?.name, learners: c?.learnerIds?.size, avgProgress: c?.count ? Math.round(c?.progressSum / c?.count) : 0 }))
    ?.sort((a, b) => b?.learners - a?.learners)
}

export const computeProgramAssessmentPerformance = async (orgId, courseIds) => {
  const { enrollments } = await getProgramLearners(orgId, courseIds)
  const attempts = []
  enrollments?.forEach((e) => {
    Object.values(e?.moduleQuizResults || {})?.forEach((r) => {
      if (r?.attemptCount) attempts.push({ score: r?.bestScore || 0, passed: !!r?.passed })
    })
    if (e?.capstoneResult?.attemptCount) attempts.push({ score: e?.capstoneResult?.bestScore || 0, passed: !!e?.capstoneResult?.passed })
  })
  const totalCompleted = attempts?.length
  const avgScore = totalCompleted ? Math.round(attempts.reduce((s, a) => s + a?.score, 0) / totalCompleted) : 0
  const passCount = attempts?.filter((a) => a?.passed)?.length || 0
  const passRate = totalCompleted ? Math.round((passCount / totalCompleted) * 1000) / 10 : 0
  return { totalCompleted, avgScore, passRate }
}

export const computeSingleProgramAnalytics = async (orgId, program, courses) => {
  const courseIds = program?.courseIds || []
  const { enrollments } = await getProgramLearners(orgId, courseIds)
  const { certificates } = await getProgramCertificates(orgId, courseIds)
  const { orders } = await listOrgOrders(orgId)

  const learnerIds = new Set(enrollments?.map((e) => e?.userId))
  const avgProgress = enrollments?.length
    ? Math.round(enrollments.reduce((s, e) => s + (e?.progressPercent || 0), 0) / enrollments.length)
    : 0
  const completedCount = enrollments?.filter((e) => (e?.progressPercent || 0) >= 100)?.length || 0
  const completionRate = enrollments?.length ? Math.round((completedCount / enrollments.length) * 100) : 0

  // Revenue matched via product title, same courseId-less-orders limitation as coursePerformanceHelpers.js
  const programCourseTitles = new Set(courses?.filter((c) => courseIds?.includes(c?.courseId))?.map((c) => c?.title))
  const revenue = orders?.filter((o) => o?.status === "paid" && programCourseTitles.has(o?.productTitle))
    ?.reduce((sum, o) => sum + (o?.amount || 0), 0) || 0

  return { totalLearners: learnerIds.size, avgProgress, completionRate, credentialsIssued: certificates?.length || 0, revenue }
}